import { useCallback, useMemo } from 'react';
import type { CashSessionRow } from '../../infra/cashSessionsRepo';
import {
  countActiveSessionInHistory,
  mergeActiveSessionIntoHistoryPage,
  type CashClosuresListQuery,
} from '../../infra/cashHistoryQuery';
import { fetchRemoteCashClosuresCount, fetchRemoteCashClosuresHistory } from '../../../reports/infra/reportsRemoteRepo';
import { isOnline } from '../../../shell/connectivity/connectivityInfra';
import { usePaginatedRemoteList } from '../../../shared/data/usePaginatedRemoteList';
import { CASH_CLOSURES_PAGE_SIZE_STORAGE_KEY } from '../cashList.constants';
import type { CashDifferenceFilter, CashHistorySort, CashOperatorFilter } from '../cashList.constants';

export type { CashClosuresListQuery } from '../../infra/cashHistoryQuery';

async function resolveCashHistoryPage(
  query: CashClosuresListQuery,
  paging: { limit: number; offset: number }
): Promise<CashSessionRow[]> {
  if (!query.branchId) return [];

  if (!(await isOnline())) {
    throw new Error('Sin conexión para cargar el historial');
  }

  const remote = await fetchRemoteCashClosuresHistory(query.branchId, {
    fromIso: query.fromIso,
    toIso: query.toIso,
    limit: paging.limit,
    offset: paging.offset,
    openedBy: query.openedBy,
    differenceFilter: query.differenceFilter,
    sort: query.sort,
  });
  if (remote) return remote;

  // ADR-017: sin fallback local — un fallo remoto es error explícito, no vacío.
  throw new Error('No se pudo cargar el historial de cierres');
}

async function resolveCashClosuresCount(query: CashClosuresListQuery): Promise<number> {
  if (!query.branchId) return 0;

  if (!(await isOnline())) {
    throw new Error('Sin conexión para contar el historial');
  }

  const remote = await fetchRemoteCashClosuresCount(query.branchId, {
    fromIso: query.fromIso,
    toIso: query.toIso,
    openedBy: query.openedBy,
    differenceFilter: query.differenceFilter,
  });
  if (remote != null) return remote;

  throw new Error('No se pudo contar el historial de cierres');
}

type Params = {
  branchId: string | null;
  fromIso: string;
  toIso: string;
  operatorFilter: CashOperatorFilter;
  differenceFilter: CashDifferenceFilter;
  historySort: CashHistorySort;
  activeSession?: CashSessionRow | null;
};

export function useCashHistoryListFlow({
  branchId,
  fromIso,
  toIso,
  operatorFilter,
  differenceFilter,
  historySort,
  activeSession,
}: Params) {
  const query = useMemo<CashClosuresListQuery>(
    () => ({
      branchId: branchId ?? '',
      fromIso,
      toIso,
      openedBy: operatorFilter === 'all' ? null : operatorFilter,
      differenceFilter,
      sort: historySort,
    }),
    [branchId, fromIso, toIso, operatorFilter, differenceFilter, historySort]
  );

  const criteriaKey = useMemo(
    () =>
      `${branchId ?? ''}|${fromIso}|${toIso}|${operatorFilter}|${differenceFilter}|${historySort}`,
    [branchId, fromIso, toIso, operatorFilter, differenceFilter, historySort]
  );

  const fetchCashHistoryPage = useCallback(
    async ({
      limit,
      offset,
      query: q,
    }: {
      limit: number;
      offset: number;
      query: CashClosuresListQuery;
    }): Promise<CashSessionRow[]> => resolveCashHistoryPage(q, { limit, offset }),
    []
  );

  const countCashHistory = useCallback(
    async (q: CashClosuresListQuery): Promise<number> => resolveCashClosuresCount(q),
    []
  );

  const paging = usePaginatedRemoteList<CashSessionRow, CashClosuresListQuery>({
    storageKey: CASH_CLOSURES_PAGE_SIZE_STORAGE_KEY,
    defaultPageSize: 25,
    criteriaKey,
    query,
    enabled: Boolean(branchId),
    logLabel: 'CashHistory',
    fetchPage: fetchCashHistoryPage,
    count: countCashHistory,
  });

  const cashHistory = useMemo(
    () =>
      mergeActiveSessionIntoHistoryPage(
        paging.items,
        activeSession,
        query,
        paging.pageIndex
      ),
    [paging.items, paging.pageIndex, activeSession, query]
  );

  const historyTotalCount = useMemo(
    () =>
      countActiveSessionInHistory(
        paging.totalCount,
        paging.items,
        activeSession,
        query,
        paging.pageIndex
      ),
    [paging.totalCount, paging.items, paging.pageIndex, activeSession, query]
  );

  return {
    cashHistory,
    historyPageIndex: paging.pageIndex,
    historyPageSize: paging.pageSize,
    setHistoryPageSize: paging.setPageSize,
    historyHasMore: paging.hasMore,
    historyLoading: paging.loading,
    historyTotalCount,
    loadHistoryPreviousPage: paging.loadPreviousPage,
    loadHistoryNextPage: paging.loadNextPage,
    goToHistoryPage: paging.goToPage,
    reloadHistory: paging.load,
    reloadHistoryFromStart: paging.reloadFromStart,
    historyError: paging.error,
    historyScrollRef: paging.scrollViewRef,
  };
}
